import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  ChevronDown, ChevronUp, LayoutDashboard, LayoutGrid, Users, User, UserPlus, Contact,
  CheckSquare, BellRing, MessageCircle, CalendarClock, Workflow, BarChart3, ShieldCheck,
  Settings, Megaphone, Sparkles, FileText, History, Wallet, LifeBuoy, Mail, Inbox, Radio,
  Target, Building2, Code2, Trash2, type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth, type PlanModule } from '@/contexts/AuthContext';

interface SidebarProps {
  collapsed: boolean;
}

// A main module plus its (already permission-filtered) sub-modules.
interface MenuNode extends PlanModule {
  children: PlanModule[];
}

// Strip a redundant parent-name prefix from a sub-module's display label
// (e.g. "WhatsApp Inbox" under "WhatsApp Chat" → "Inbox"). The full name is
// still used as the permission key; this only affects what the user reads.
const childLabel = (parentName: string, childName: string) => {
  const firstWord = parentName.split(' ')[0];
  return childName.startsWith(firstWord + ' ')
    ? childName.slice(firstWord.length + 1)
    : childName;
};

// DB module icons are RemixIcon class names; render the matching thin-line
// lucide icon for a consistent look. Unmapped icons fall back to RemixIcon,
// so a new module added in the catalog still shows its own icon.
const LUCIDE_BY_REMIX: Record<string, LucideIcon> = {
  'ri-dashboard-line': LayoutDashboard,
  'ri-apps-line': LayoutGrid,
  'ri-contacts-line': Users,
  'ri-team-line': Users,
  'ri-contacts-book-line': Contact,
  'ri-user-line': User,
  'ri-user-add-line': UserPlus,
  'ri-task-line': CheckSquare,
  'ri-notification-line': BellRing,
  'ri-chat-1-line': MessageCircle,
  'ri-message-3-line': MessageCircle,
  'ri-time-line': CalendarClock,
  'ri-flow-chart': Workflow,
  'ri-bar-chart-line': BarChart3,
  'ri-shield-line': ShieldCheck,
  'ri-shield-user-line': ShieldCheck,
  'ri-shield-star-line': ShieldCheck,
  'ri-file-shield-2-line': ShieldCheck,
  'ri-settings-line': Settings,
  'ri-settings-3-line': Settings,
  'ri-megaphone-line': Megaphone,
  'ri-sparkling-2-line': Sparkles,
  'ri-file-list-line': FileText,
  'ri-file-list-2-line': FileText,
  'ri-file-list-3-line': FileText,
  'ri-history-line': History,
  'ri-wallet-line': Wallet,
  'ri-lifebuoy-line': LifeBuoy,
  'ri-mail-line': Mail,
  'ri-inbox-line': Inbox,
  'ri-broadcast-line': Radio,
  'ri-target-line': Target,
  'ri-building-line': Building2,
  'ri-code-line': Code2,
  'ri-delete-bin-line': Trash2,
};

function MenuIcon({ icon }: { icon: string }) {
  const Icon = LUCIDE_BY_REMIX[icon];
  if (Icon) return <Icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.75} />;
  return <i className={cn(icon, 'text-[20px] leading-none flex-shrink-0')} />;
}

export function Sidebar({ collapsed }: SidebarProps) {
  const location = useLocation();
  const { user, permissions, planMenu, brand } = useAuth();
  const userRole = user?.role ?? 'agent';
  const isAdmin = userRole === 'admin';

  // Role permission check. Plan gating already happened server-side —
  // planMenu only contains modules in the tenant's subscription plan — so
  // here we only apply the per-role view permission. Admin sees everything
  // in-plan; everyone else is DEFAULT-DENY: a module is visible only if their
  // Role explicitly grants view=true. An unmapped module stays hidden (least
  // privilege) — the old `!== false` let a limited role see every module it
  // wasn't explicitly denied, so e.g. a Manager saw admin-level menus.
  const canSee = (name: string) => {
    if (isAdmin) return true;
    return permissions?.[name]?.view === true;
  };

  // Build the menu tree 100% from the DB catalog: mains (type 'M') ordered
  // by orderNumber, each with its visible sub-modules (type 'S') nested by
  // parentId. Nothing about the menu is hardcoded — add a module in the
  // super-admin catalog + plan and it appears here on next refresh.
  const mods = planMenu ?? [];
  const subsByParent = new Map<string, PlanModule[]>();
  for (const m of mods) {
    if (m.type === 'S' && m.parentId) {
      const arr = subsByParent.get(m.parentId) ?? [];
      arr.push(m);
      subsByParent.set(m.parentId, arr);
    }
  }
  const tree: MenuNode[] = mods
    .filter((m) => m.type === 'M')
    .sort((a, b) => a.orderNumber - b.orderNumber)
    .map((main) => ({
      ...main,
      children: (subsByParent.get(main.id) ?? [])
        .filter((c) => canSee(c.name))
        .sort((a, b) => a.orderNumber - b.orderNumber),
    }));

  const visibleItems = tree.filter((item) => {
    if (item.children.length > 0) return true; // group with ≥1 visible child
    if (!item.pageLink || item.pageLink === '#') return false; // pure group, no children
    return canSee(item.name); // standalone link
  });

  const isActive = (href: string) =>
    location.pathname === href ||
    (href !== '/dashboard' && href !== '#' && location.pathname.startsWith(href + '/'));

  const isGroupActive = (item: MenuNode) => item.children.some((c) => isActive(c.pageLink));

  const getDefaultOpen = (): Record<string, boolean> => {
    const open: Record<string, boolean> = {};
    visibleItems.forEach((item) => {
      if (item.children.length > 0 && item.children.some((c) => isActive(c.pageLink))) {
        open[item.id] = true;
      }
    });
    return open;
  };

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(getDefaultOpen);
  const toggleGroup = (id: string) =>
    setOpenGroups((prev) => ({ ...prev, [id]: !prev[id] }));

  return (
    <div
      className={cn(
        'flex h-full flex-col bg-white border-r border-gray-100 flex-shrink-0 overflow-hidden transition-all duration-300 ease-in-out',
        collapsed ? 'w-[60px]' : 'w-60',
      )}
    >
      {/* Brand — tenant logo + name when set, BrixLead fallback otherwise */}
      <div className="flex h-16 items-center border-b border-gray-100 px-4 flex-shrink-0 overflow-hidden">
        {collapsed ? (
          brand?.logoSquare ? (
            <img
              src={brand.logoSquare}
              alt={brand.organizationName ?? 'Logo'}
              className="h-10 w-10 object-contain mx-auto"
            />
          ) : (
            <span className="text-[#6366F1] font-bold text-lg mx-auto">
              {(brand?.organizationName ?? 'BrixLead').charAt(0).toUpperCase()}
            </span>
          )
        ) : brand?.logoRectangular || brand?.logoSquare ? (
          <img
            src={brand.logoRectangular || brand.logoSquare!}
            alt={brand.organizationName ?? 'Logo'}
            className="max-h-14 w-auto max-w-full object-contain"
          />
        ) : (
          <span className="text-xl font-bold text-[#6366F1] tracking-tight whitespace-nowrap truncate">
            {brand?.organizationName || 'BrixLead'}
          </span>
        )}
      </div>

      {/* Nav — rendered entirely from the DB module catalog */}
      <nav className="no-scrollbar flex-1 overflow-y-auto py-3 px-2 space-y-0.5">
        {visibleItems.map((item) => {
          const icon = item.icon || 'ri-circle-line';

          if (item.children.length > 0) {
            const groupActive = isGroupActive(item);
            const isOpen = !collapsed && (openGroups[item.id] ?? false);

            return (
              <div key={item.id}>
                <button
                  onClick={() => !collapsed && toggleGroup(item.id)}
                  title={collapsed ? item.name : undefined}
                  className={cn(
                    'flex items-center w-full rounded-lg px-3 py-2 text-sm transition-colors',
                    collapsed ? 'justify-center' : 'justify-between gap-2.5',
                    groupActive
                      ? 'bg-[#6366F1] text-white'
                      : 'text-[#64748B] hover:bg-gray-50 hover:text-[#1E293B]',
                  )}
                >
                  <div className={cn('flex items-center', collapsed ? '' : 'gap-3')}>
                    <MenuIcon icon={icon} />
                    {!collapsed && <span className="whitespace-nowrap">{item.name}</span>}
                  </div>
                  {!collapsed && (
                    isOpen
                      ? <ChevronUp className="h-4 w-4 flex-shrink-0" />
                      : <ChevronDown className="h-4 w-4 flex-shrink-0" />
                  )}
                </button>

                {isOpen && (
                  <div className="ml-4 mt-0.5 mb-1 border-l-2 border-gray-100 pl-3 space-y-0.5">
                    {item.children.map((child) => (
                      <Link
                        key={child.id}
                        to={child.pageLink}
                        className={cn(
                          'block rounded-md px-3 py-1.5 text-sm transition-colors whitespace-nowrap',
                          isActive(child.pageLink)
                            ? 'text-[#6366F1] bg-indigo-50'
                            : 'text-[#64748B] hover:text-[#1E293B] hover:bg-gray-50',
                        )}
                      >
                        {childLabel(item.name, child.name)}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          }

          const active = isActive(item.pageLink);
          return (
            <Link
              key={item.id}
              to={item.pageLink}
              title={collapsed ? item.name : undefined}
              className={cn(
                'flex items-center rounded-lg px-3 py-2 text-sm transition-colors',
                collapsed ? 'justify-center' : 'gap-3',
                active
                  ? 'bg-[#6366F1] text-white'
                  : 'text-[#64748B] hover:bg-gray-50 hover:text-[#1E293B]',
              )}
            >
              <MenuIcon icon={icon} />
              {!collapsed && <span className="whitespace-nowrap">{item.name}</span>}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
